// Implements: F-UIX-020, F-UIX-007, F-UIX-008, F-UIX-009
/**
 * The design profile editor and its live side (UX-09). Pure presentation.
 *
 * Owner round 4 ("sigue muy diferente al mockup"): the form is as compact as the mockup's "Editar
 * perfil" card. Visible at once: the name, four colours in a 2x2 grid (principal, secundario, fondo,
 * texto; mockup v3 P5) with the three state colours folded under them, one typography for titles and text, the corners as a small
 * segmented control, the logo, and where the profile is active. Behind one press: the reference
 * images, and "Más opciones" (both fonts apart, base size, scale, register, density and the
 * instructions). Long explanations live in (i) tooltips; a field carries one short line at most.
 *
 * Every value is a prop and every change is a callback, so a refused save leaves the person's input
 * where it was (F-UIX-008): only the container knows it was refused. Fonts, sizes, scale, register
 * and density are closed selects, the radius a closed segmented choice, and the seven colours are text
 * fields in the server's `#rgb|#rrggbb` grammar. The contrast of every pair is computed on each
 * render, with no request, and the save stays disabled while any pair is below 4.5:1 or not a colour
 * yet; a failing state colour keeps its group open so the person can see it. Saving always creates
 * a version: nothing here edits one in place.
 *
 * Audit P5 (mockup v3 "Ver perfil"): `mode="view"` is the same editor for a profile the server does not
 * let the person edit. Every control is disabled (still named and carrying its stored value, so a screen
 * reader reads it), the disclosures and tooltips still open, and in place of the save there is only what
 * the container offers (`duplicateAction`, "Duplicar y editar"). A notice above the fields says why; it
 * takes the focus when `notice.focus` is set (the person opened the profile from the list).
 *
 * Owner 2026-10-19 (answer 4A): an editable profile offers "Duplicar y editar" too, beside the save
 * (`duplicateAction`, the instant copy), and the copy opens announcing itself (`announcement`).
 */

import { useEffect, useRef } from "react";
import type { ReactElement, ReactNode } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Badge } from "../../../shared/ui/atoms/Badge";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { dayText } from "../../../shared/ui/dayText";
import { Input } from "../../../shared/ui/atoms/Input";
import { Textarea } from "../../../shared/ui/atoms/Textarea";
import { Disclosure } from "../../../shared/ui/molecules/Disclosure";
import { Field, describedBy } from "../../../shared/ui/molecules/Field";
import { InfoTip } from "../../../shared/ui/molecules/InfoTip";
import { SegmentedControl } from "../../../shared/ui/molecules/SegmentedControl";
import type { PaletteRole, ProfileVersionValues } from "../../../types/design";
import {
  BASE_SIZES,
  DENSITIES,
  OFFERED_RADII,
  type PairName,
  REGISTERS,
  TYPE_SCALES,
} from "../model/profileModel";
import { ClosedSelect } from "./ClosedSelect";
import type { ClosedOption } from "./ClosedSelect";
import { ColourField } from "./ColourField";
import { ContrastSummary } from "./ContrastSummary";
import { FontSample } from "./FontSample";
import { FontSelect } from "./FontSelect";
import { evaluatePairs } from "./contrastPairs";
import {
  DENSITY_LABELS,
  RADIUS_LABELS,
  REGISTER_LABELS,
  ROLE_LABELS,
  SCALE_LABELS,
} from "./editorOptions";
import type { DesignKey, Labels } from "./editorOptions";
import { RADIUS_PX } from "./previewTokens";
import { ProfilePreview } from "./ProfilePreview";
import styles from "./ProfileEditor.module.css";

export interface ProfileEditorProps {
  /** `view`: a profile the server marks read-only, shown with every control disabled (audit P5). */
  readonly mode: "create" | "edit" | "view";
  readonly name: string;
  readonly onNameChange: (value: string) => void;
  /** The scopes a new profile may be created in (the server's `may_edit`, W2l); shown while creating. */
  readonly scopeOptions?: readonly ClosedOption[] | undefined;
  readonly scope?: string | undefined;
  readonly onScopeChange?: ((value: string) => void) | undefined;
  /** The latest stored version, shown as small meta in the head; `null` while creating. */
  readonly currentVersion: number | null;
  /**
   * When the shown version was saved and its author's current full name (default P5-4), said next
   * to the version; the date alone once the author can no longer be named.
   */
  readonly savedBy?: { readonly at: string; readonly name: string | null } | undefined;
  readonly values: ProfileVersionValues;
  readonly onValuesChange: (values: ProfileVersionValues) => void;
  readonly onSubmit: () => void;
  /** A save is in flight: nothing can be sent until it answers. */
  readonly pending: boolean;
  /** The server's refusal of the palette, shown next to it. */
  readonly paletteError?: string | undefined;
  /** Any other refusal of the save. */
  readonly formError?: string | undefined;
  /** The version the last save created, announced once it exists. */
  readonly savedVersion?: number | null | undefined;
  /** The stored logo's address, drawn in the preview. */
  readonly logoSrc?: string | undefined;
  /** The logo's drop zone (a stored profile only); a new profile says it comes after saving. */
  readonly logo?: ReactNode;
  /** The reference images (a stored profile only), behind their disclosure. */
  readonly references?: ReactNode;
  /** How many reference images the profile holds, for the disclosure's label. */
  readonly referenceCount?: number | undefined;
  /** "Activo en": the switch of the profile's scope (a stored profile only). */
  readonly activeIn?: ReactNode;
  /** The stored profile's name with its in-place rename (mockup v3 P5); else the name as text. */
  readonly nameField?: ReactNode;
  /** "Borrar perfil" of a stored profile the person may edit (owner 2026-10-19); none while creating. */
  readonly deleteAction?: ReactNode;
  /** Why the profile cannot be edited, above the fields (`view` only); `focus` moves the focus to it. */
  readonly notice?: { readonly text: string; readonly focus: boolean } | undefined;
  /** "Duplicar y editar", the instant copy: in `view` when the person may create somewhere, and in `edit`. */
  readonly duplicateAction?: ReactNode;
  /** A sentence announced once as the editor opens (a just-made copy, owner 2026-10-19). */
  readonly announcement?: string | undefined;
}

/** The four colours shown at once (the mockup v3 2x2 grid), and the three behind "Colores de estado". */
const MAIN_ROLES: readonly PaletteRole[] = ["primary", "secondary", "surface", "text"];
const STATE_ROLES: readonly PaletteRole[] = ["success", "warning", "danger"];
/** The contrast pairs whose foreground is a state colour. */
const STATE_PAIRS: readonly PairName[] = ["success-on-surface", "warning-on-surface", "danger-on-surface"];
/** The select's value when titles and text use different fonts. */
const CUSTOM_FONT = "custom";

function optionsOf<List extends readonly (string | number)[]>(
  values: List,
  labels: Labels<List>,
  t: (key: DesignKey) => string,
): ClosedOption[] {
  return values.map((value) => {
    const member = String(value) as `${List[number]}`;
    return { value: member, label: t(labels[member]) };
  });
}

/** A field label with its (i): the long explanation as a tooltip. */
function LabelRow({
  htmlFor,
  id,
  label,
  unit,
  help,
}: {
  readonly htmlFor?: string;
  readonly id?: string;
  readonly label: string;
  /** A unit after the label, as the mockup's "Esquinas (px)". */
  readonly unit?: string;
  readonly help: string;
}): ReactElement {
  return (
    <span className={styles.labelRow}>
      {htmlFor === undefined ? (
        <span id={id} className={styles.label}>
          {label}
        </span>
      ) : (
        <label htmlFor={htmlFor} className={styles.label}>
          {label}
        </label>
      )}
      {unit === undefined ? null : <span className={styles.unit}>{`(${unit})`}</span>}
      <InfoTip subject={label} content={help} />
    </span>
  );
}

export function ProfileEditor({
  mode,
  name,
  onNameChange,
  scopeOptions = [],
  scope = "",
  onScopeChange,
  currentVersion,
  savedBy,
  values,
  onValuesChange,
  onSubmit,
  pending,
  paletteError,
  formError,
  savedVersion,
  logoSrc,
  logo,
  references,
  referenceCount = 0,
  activeIn,
  nameField,
  deleteAction,
  notice,
  duplicateAction,
  announcement,
}: ProfileEditorProps): ReactElement {
  const { t } = useTranslation();
  const readOnly = mode === "view";
  // Nothing can change while a save is in flight, nor ever in a profile shown read-only.
  const locked = pending || readOnly;
  const noticeRef = useRef<HTMLDivElement>(null);
  const focusNotice = notice?.focus === true;
  useEffect(() => {
    if (focusNotice) noticeRef.current?.focus();
  }, [focusNotice]);
  const pairs = evaluatePairs(values.palette);
  const blocked = pairs.some((pair) => !pair.passes) || (mode === "create" && name.trim() === "");
  // A failing state colour (or one the server's refusal names) must stay in sight.
  const statePairs = pairs.filter((pair) => STATE_PAIRS.includes(pair.name));
  const stateBlocked =
    statePairs.some((pair) => !pair.passes) ||
    (paletteError !== undefined && statePairs.some((pair) => paletteError.includes(t(pair.label))));
  const set = (patch: Partial<ProfileVersionValues>): void => {
    onValuesChange({ ...values, ...patch });
  };
  const colourField = (role: PaletteRole): ReactElement => (
    <ColourField
      key={role}
      id={`design-colour-${role}`}
      label={t(ROLE_LABELS[role])}
      value={values.palette[role]}
      onChange={(colour) => {
        set({ palette: { ...values.palette, [role]: colour } });
      }}
      disabled={locked}
    />
  );
  const sizeOptions = BASE_SIZES.map((size) => ({
    value: String(size),
    label: `${String(size)} ${t("design.editor.pixels")}`,
  }));
  const sameFont = values.heading_font === values.body_font;
  const customFont = sameFont ? [] : [{ value: CUSTOM_FONT, label: t("design.font.custom") }];
  // Mockup v3 P5: 0 / 4 / 8 / 12 px are offered; a version stored with the old 16 px keeps it, shown
  // as an extra pressed segment until the person picks another corner (stored versions are immutable).
  const radiusOptions: { value: string; label: string; name: string }[] = OFFERED_RADII.map((radius) => {
    const px = (RADIUS_PX[radius] ?? "0").replace("px", "");
    return { value: radius, label: px, name: `${t(RADIUS_LABELS[radius])}, ${px} ${t("design.editor.pixels")}` };
  });
  if (values.radius === "large") {
    const legacy = t("design.radius.legacy");
    radiusOptions.push({ value: "large", label: legacy, name: `${legacy}, 16 ${t("design.editor.pixels")}` });
  }
  const referencesLabel = `${t("design.editor.references")} (${String(referenceCount)})`;

  return (
    <div className={styles.layout}>
      <form
        className={styles.editor}
        aria-labelledby="design-editor-title"
        noValidate
        onSubmit={(event) => {
          // A browser does not submit implicitly through a disabled default button,
          // and the guard repeats it so no path sends a failing palette.
          event.preventDefault();
          if (!blocked && !locked) onSubmit();
        }}
      >
        <div className={styles.head}>
          <h2 id="design-editor-title" className={styles.title}>
            {mode === "create"
              ? t("design.editor.titleNew")
              : readOnly
                ? t("design.readOnly.title")
                : t("design.editor.titleEdit")}
          </h2>
          {currentVersion === null ? null : (
            <span className={styles.meta}>
              {t("design.editor.version", { version: currentVersion })}
              {savedBy === undefined ? null : (
                <span>
                  {" · "}
                  {savedBy.name === null
                    ? t("design.editor.savedOn", { date: dayText(savedBy.at) })
                    : t("design.editor.savedBy", { date: dayText(savedBy.at), name: savedBy.name })}
                </span>
              )}
            </span>
          )}
        </div>
        <div className={styles.body}>
          {notice === undefined ? null : (
            <div ref={noticeRef} role="note" tabIndex={-1} className={styles.notice}>
              <Icon name="lock" size="sm" />
              <p className={styles.noticeText}>{notice.text}</p>
            </div>
          )}
          {mode === "create" ? (
            <>
              <Field id="design-name" label={t("design.editor.name")}>
                <Input id="design-name" value={name} onChange={onNameChange} disabled={locked} />
              </Field>
              {scopeOptions.length === 0 ? null : (
                <ClosedSelect
                  id="design-scope"
                  label={t("design.editor.scope")}
                  options={scopeOptions}
                  value={scope}
                  onChange={(value) => {
                    onScopeChange?.(value);
                  }}
                  disabled={locked}
                />
              )}
            </>
          ) : (
            (nameField ?? (
              <div className={styles.field}>
                <span className={styles.label}>{t("design.editor.name")}</span>
                <div className={styles.nameRow}>
                  <p className={styles.readOnlyValue}>{name}</p>
                  {readOnly ? <Badge tone="neutral">{t("design.list.readOnly")}</Badge> : null}
                </div>
              </div>
            ))
          )}

          <fieldset className={styles.palette}>
            <legend className={styles.srOnly}>{t("design.editor.palette")}</legend>
            <div className={styles.two} data-testid="design-palette-main">
              {MAIN_ROLES.map(colourField)}
            </div>
            <Disclosure
              id="design-state-colours"
              label={t("design.palette.status")}
              forceOpen={stateBlocked}
              triggerClassName={styles.stateTrigger}
              regionClassName={styles.stateRegion}
            >
              <div className={styles.two}>{STATE_ROLES.map(colourField)}</div>
            </Disclosure>
            {paletteError === undefined ? null : (
              <p className={styles.error} role="alert">
                {paletteError}
              </p>
            )}
          </fieldset>

          <div className={styles.two}>
            <div className={styles.field}>
              <LabelRow htmlFor="design-typography" label={t("design.editor.typography")} help={t("design.help.type")} />
              <FontSelect
                id="design-typography"
                value={sameFont ? values.body_font : CUSTOM_FONT}
                extra={customFont}
                onChange={(value) => {
                  if (value !== CUSTOM_FONT) set({ heading_font: value, body_font: value });
                }}
                disabled={locked}
              />
            </div>
            <div className={styles.field}>
              <LabelRow
                id="design-radius-label"
                label={t("design.editor.radius")}
                unit={t("design.editor.pixels")}
                help={t("design.help.radius")}
              />
              <SegmentedControl<string>
                label={t("design.editor.radius")}
                options={radiusOptions}
                value={values.radius}
                onChange={(radius) => {
                  set({ radius });
                }}
                disabled={locked}
              />
            </div>
          </div>

          <FontSample font={values.body_font} />

          <div className={styles.field}>
            <LabelRow id="design-logo-label" label={t("design.editor.logo")} help={t("design.help.logo")} />
            {logo ?? <p className={styles.later}>{t("design.editor.logoLater")}</p>}
          </div>

          <div className={styles.disclosures}>
            {references === undefined ? null : (
              <Disclosure id="design-references" label={referencesLabel} regionClassName={styles.region}>
                {references}
              </Disclosure>
            )}
            <Disclosure id="design-more" label={t("design.editor.more")} regionClassName={styles.region}>
              <div className={styles.two}>
                <Field id="design-heading-font" label={t("design.editor.headingFont")}>
                  <FontSelect
                    id="design-heading-font"
                    value={values.heading_font}
                    onChange={(value) => {
                      set({ heading_font: value });
                    }}
                    disabled={locked}
                  />
                </Field>
                <Field id="design-body-font" label={t("design.editor.bodyFont")}>
                  <FontSelect
                    id="design-body-font"
                    value={values.body_font}
                    onChange={(value) => {
                      set({ body_font: value });
                    }}
                    disabled={locked}
                  />
                </Field>
                <ClosedSelect
                  id="design-base-size"
                  label={t("design.editor.baseSize")}
                  options={sizeOptions}
                  value={String(values.base_size)}
                  onChange={(value) => {
                    set({ base_size: Number(value) });
                  }}
                  disabled={locked}
                />
                <ClosedSelect
                  id="design-scale"
                  label={t("design.editor.scale")}
                  options={optionsOf(TYPE_SCALES, SCALE_LABELS, t)}
                  value={String(values.scale)}
                  onChange={(value) => {
                    set({ scale: Number(value) });
                  }}
                  disabled={locked}
                />
                <ClosedSelect
                  id="design-register"
                  label={t("design.editor.register")}
                  options={optionsOf(REGISTERS, REGISTER_LABELS, t)}
                  value={values.register}
                  onChange={(value) => {
                    set({ register: value });
                  }}
                  disabled={locked}
                />
                <ClosedSelect
                  id="design-density"
                  label={t("design.editor.density")}
                  options={optionsOf(DENSITIES, DENSITY_LABELS, t)}
                  value={values.density}
                  onChange={(value) => {
                    set({ density: value });
                  }}
                  disabled={locked}
                />
              </div>
              <Field id="design-instructions" label={t("design.editor.instructions")} hint={t("design.help.instructions")}>
                <Textarea
                  id="design-instructions"
                  describedBy={describedBy("design-instructions", { hint: t("design.help.instructions") })}
                  value={values.instructions}
                  onChange={(instructions) => {
                    set({ instructions });
                  }}
                  disabled={locked}
                  rows={3}
                />
              </Field>
            </Disclosure>
          </div>

          {activeIn ?? null}

          {formError === undefined ? null : (
            <p className={styles.error} role="alert">
              {formError}
            </p>
          )}
          {savedVersion === undefined || savedVersion === null ? (
            announcement === undefined ? null : (
              <p role="status" className={styles.saved}>
                {announcement}
              </p>
            )
          ) : (
            <p role="status" className={styles.saved}>{`${t("design.editor.saved")} ${String(savedVersion)}`}</p>
          )}
          {readOnly ? (
            duplicateAction === undefined ? null : <div className={styles.actions}>{duplicateAction}</div>
          ) : (
            <div className={styles.actions}>
              <Button variant="primary" type="submit" loading={pending} disabled={blocked}>
                {mode === "create" ? t("design.editor.create") : t("design.editor.save")}
              </Button>
              {duplicateAction ?? null}
              {deleteAction ?? null}
            </div>
          )}
        </div>
      </form>
      <div className={styles.aside}>
        <ProfilePreview name={name} values={values} logoSrc={logoSrc} />
        <ContrastSummary pairs={pairs} />
      </div>
    </div>
  );
}
